import React, { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../hooks/useSystemMessage';
import { fetchAssistants } from '../../services/api/virtualAssistant';
import SharedVirtualAssistants from './SharedVirtualAssistants';
import AssistantMergeModal from "../../components/AssistantMergeModal";
import CoOwnerModal from "../../components/CoOwnerModal";
import { Assistant } from "../../types/assistant";
import VirtualAssistantsContent from './VirtualAssistantsContent';
import VirtualAssistantModals from './VirtualAssistantModals';
import { useVirtualAssistantHandlers } from './useVirtualAssistantHandlers';

interface SystemConfig {
  Id: string;
  ConfigValue: string;
}

const CACHE_DURATION = 2 * 24 * 60 * 60 * 1000
const DEFAULT_PAGE_SIZE = 6;

const CONFIG_ID_MAPPING: { [key: string]: string } = {
  'maxpublishcount': 'author_publish_limit',
  'maxprompts': 'messages_per_month_limit',
  'va_publish_limit': 'va_publish_limit'
};

const getCachedData = (key: string) => {
  const item = sessionStorage.getItem(key);
  if (!item) return null;

  const { value, timestamp } = JSON.parse(item);
  if (Date.now() - timestamp > CACHE_DURATION) {
    sessionStorage.removeItem(key);
    return null;
  }
  return value;
};

const VirtualAssistantsList = () => {
  // Basic states
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [processedNoteMessage, setProcessedNoteMessage] = useState('');
  const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
  const optionsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inference_url, setInferenceURL] = useState('');
  const [published_count, setPublishedCount] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const hasTypedRef = useRef(false);

  const [activeTab, setActiveTab] = useState('my-assistants');

  const welcomeMessage = useSystemMessage('VA Builder welcome message', '');
  const noteMessage = useSystemMessage('VA Builder Notes', '');

  const [systemConfig, setSystemConfig] = useState<SystemConfig[]>([]);
  useEffect(() => {
      const config = getCachedData('systemConfig');
      if (config) {
        setSystemConfig(config);
      }
    }, []);
  
  let KaiBlobLocation = systemConfig.find(item => item.Id === 'blob_root_folder')?.ConfigValue;
  const defaultAIFace = KaiBlobLocation + "/logo/boticondefault.svg";

  // Helper function to get publish limit from system config
  const getPublishLimit = () => {
    const config = systemConfig.find(item => item.Id === 'va_publish_limit');
    return config ? parseInt(config.ConfigValue) : 5; // fallback to 5 if not found
  };

  const [showNote, setShowNote] = useState(false);
  const handleToggleNote = () => {
    setShowNote((prev) => !prev);
  }; 
  useEffect(() => {
    if (published_count >= getPublishLimit()) {
      setShowNote(true);
    }
  }, [published_count, systemConfig]);

  // Use the custom handlers hook
  const {
    customModal,
    setCustomModal,
    showMergeModal,
    setShowMergeModal,
    mergeTarget,
    setMergeTarget,
    deleteAfterMerge,
    setDeleteAfterMerge,
    selectedAssistant,
    isCoOwnerModalOpen,
    setIsCoOwnerModalOpen,
    selectedAppId,
    selectedAppOwner,
    selectedCoOwners,
    handleAssistantClick,
    handleBuildNew,
    handleMergeClick,
    handleMerge,
    handleClone,
    handleExport,
    handleCoOwner,
    handleSaveCoOwners,
    handleCopyAppUrl,
    handleDelete,
    handleImport
  } = useVirtualAssistantHandlers(fetchAssistantsData, currentPage, pageSize, searchTerm, inference_url);

  const processMessageWithConfig = (message: string): string => {
    if (!message) return '';
    const systemConfig = getCachedData('systemConfig');
    if (!systemConfig) return message;
    return message.replace(/\[\[(.*?)\]\]/g, (match, key) => {
      const configId = CONFIG_ID_MAPPING[key.toLowerCase()] || key;
      const configItem = systemConfig.find((item: { Id: string; ConfigValue: string }) =>
        item.Id.toLowerCase() === configId.toLowerCase()
      );
      return configItem ? configItem.ConfigValue : match;
    });
  };

  useEffect(() => {
    const processedMessage = processMessageWithConfig(noteMessage);
    setProcessedNoteMessage(processedMessage);
  }, [noteMessage]);

  useEffect(() => {
    fetchAssistantsData(1, pageSize, searchTerm);
  }, [pageSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(optionsRef.current).forEach((key) => {
        if (optionsRef.current[key] && !optionsRef.current[key]?.contains(event.target as Node)) {
          setShowOptions((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      hasTypedRef.current = true;
      fetchAssistantsData(1, pageSize, searchTerm);
    }
    if (searchTerm.length === 0 && hasTypedRef.current) {
      hasTypedRef.current = false;
      fetchAssistantsData(1, pageSize, searchTerm);
    }
  }, [searchTerm]);

  const getUserName = () => localStorage.getItem('fullName') || '';
  const getNetId = () => localStorage.getItem('netId') || '';

  async function fetchAssistantsData(page: number, pageSize: number, searchTerm: string = '') {
    try {
      setLoading(true);
      const response = await fetchAssistants(getNetId(), pageSize, (page - 1) * pageSize, searchTerm);
      setAssistants(response.assistants);
      setTotalPages(response.totalPages);
      setPublishedCount(response?.published_count);
      setInferenceURL(response?.inference_url);
      localStorage.setItem('inference_url',response?.inference_url);
      if(!response?.inference_url){
        setInferenceURL('http://localhost:4000/')
      }
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to fetch assistants');
    } finally {
      setLoading(false);
    }
  }

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  // Create wrapped handlers that pass setShowOptions
  const wrappedHandleClone = (assistantId: string, assistantName: string) => {
    return handleClone(assistantId, assistantName, setShowOptions);
  };

  const wrappedHandleExport = (assistant: Assistant) => {
    return handleExport(assistant, setShowOptions);
  };

  const wrappedHandleCopyAppUrl = (assistantId: string) => {
    return handleCopyAppUrl(assistantId, setShowOptions);
  };

  const wrappedHandleDelete = (assistantId: string) => {
    return handleDelete(assistantId, setShowOptions);
  };

  return (
    <div className="container mx-auto px-4 mt-20 mb-[calc(10vh)]">
      <div className="mb-3">
        <h2 className="text-xl font-medium mb-2">Hello {getUserName()}.</h2>
        <h3 className="text-lg font-medium text-gray-800 mb-2">Welcome to the Kellogg AI Builder.</h3>
        {/* <p className="text-gray-800">{welcomeMessage}</p> */}
      </div>

       <div className={`grid gap-6 ${showNote && activeTab === 'my-assistants' ? 'grid-cols-3' : 'grid-cols-1'}`}>
        <div className={`${showNote && activeTab === 'my-assistants' ? 'col-span-2' : 'col-span-1'}`}>
          <div className="bg-white rounded-lg">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-3 pt-5">
                <button
                  onClick={() => setActiveTab('my-assistants')}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'my-assistants'
                      ? 'border-black text-black' 
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  Your Chatbots
                </button>
                <button
                  onClick={() => setActiveTab('shared-assistants')}
                  className={`pb-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'shared-assistants'
                      ? 'border-black text-black' 
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  Shared With You
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === 'my-assistants' ? (
              <VirtualAssistantsContent
                assistants={assistants}
                loading={loading}
                viewMode={viewMode}
                setViewMode={setViewMode}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                setPageSize={setPageSize}
                published_count={published_count}
                publishLimit={getPublishLimit()}
                showNote={showNote}
                handleToggleNote={handleToggleNote}
                fetchAssistantsData={fetchAssistantsData}
                handleAssistantClick={handleAssistantClick}
                showOptions={showOptions}
                setShowOptions={setShowOptions}
                optionsRef={optionsRef}
                handleCoOwner={handleCoOwner}
                handleCopyAppUrl={wrappedHandleCopyAppUrl}
                handleClone={wrappedHandleClone}
                handleExport={wrappedHandleExport}
                handleMergeClick={handleMergeClick}
                handleDelete={wrappedHandleDelete}
                defaultAIFace={defaultAIFace}
              />
            ) : (
              /* Shared VAs Component */
              <SharedVirtualAssistants />
            )}
          </div>

          {/* Action Buttons - Only show for "Your Assistants" tab */}
          {activeTab === 'my-assistants' && (
            <div className="p-6 pl-0 flex gap-4">
              <button
                onClick={handleBuildNew}
                className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faPlusCircle} className="h-4 w-4" />
                BUILD NEW
              </button>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faFileImport} className="h-4 w-4" />
                IMPORT FROM FILE
              </button>
            </div>
          )}
        </div>

        {/* Note Panel - Only show for "Your Assistants" tab when conditions are met */}
        {showNote && published_count >= getPublishLimit() && activeTab === 'my-assistants' && (
          <div className="col-span-1">
            <div className="bg-gray-100 p-6 rounded-lg h-full">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                <div className="text-sm text-gray-600">
                  <h2 className="font-medium mb-2">Note</h2>
                  <div dangerouslySetInnerHTML={{ __html: processedNoteMessage }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* All Modals */}
      <VirtualAssistantModals
        showBuildModal={showBuildModal}
        setShowBuildModal={setShowBuildModal}
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        handleImport={handleImport}
        customModal={customModal}
        setCustomModal={setCustomModal}
      />
      
      {/* Merge Modal */}
      {showMergeModal && selectedAssistant &&
       <AssistantMergeModal
        assistant={selectedAssistant}
        mergeTarget={mergeTarget}
        deleteAfterMerge={deleteAfterMerge}
        setMergeTarget={setMergeTarget}
        setDeleteAfterMerge={setDeleteAfterMerge}
        setShowMergeModal={setShowMergeModal}
        handleMerge={handleMerge}
        isDeleteEnabled={true}
      />}

      {/* Co-Owner Modal */}
      <CoOwnerModal
        isOpen={isCoOwnerModalOpen}
        onClose={() => setIsCoOwnerModalOpen(false)}
        assistantId={selectedAppId}
        appOwner={selectedAppOwner}
        onSave={handleSaveCoOwners}
        initialSelectedUsers={selectedCoOwners}
      />
    </div>
  );
};

export default VirtualAssistantsList;